/**
 * Selection Editor V2
 *
 * Combines Filters and Rank & Pick into a unified selection interface
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Badge } from '../ui/badge.tsx';
import { Plus } from 'lucide-react';
import type {
  SelectionV2,
  FilterGroup,
  RankPickSpec,
  PreviewResult
} from '../../types/selectionV2.ts';
import {
  DEFAULT_SELECTION_V2,
  createFilterGroup
} from '../../types/selectionV2.ts';
import FilterGroupEditor from './FilterGroupEditor.tsx';
import RankPickEditor from './RankPickEditor.tsx';

interface SelectionEditorV2Props {
  config: SelectionV2;
  onChange: (config: SelectionV2) => void;
  formulas?: Array<{ id: string; name: string; formula: string }>;
  universeConfig?: any;
  apiBaseUrl?: string;
  onboardingStep?: number;
  onOnboardingComplete?: () => void;
}

const SelectionEditorV2: React.FC<SelectionEditorV2Props> = ({
  config,
  onChange,
  formulas = [],
  universeConfig,
  apiBaseUrl = 'http://localhost:4998',
  onboardingStep = 4,
  onOnboardingComplete
}) => {
  const [preview, setPreview] = useState<PreviewResult | null>(null);

  // Ensure config has default structure
  const safeConfig: SelectionV2 = {
    ...DEFAULT_SELECTION_V2,
    ...config,
    filters: {
      groups: config?.filters?.groups || []
    },
    rankPick: {
      ...DEFAULT_SELECTION_V2.rankPick,
      ...config?.rankPick
    }
  };

  // Debounced preview
  useEffect(() => {
    const timer = setTimeout(() => {
      if (safeConfig.rankPick.formula) {
        fetchPreview();
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeConfig, universeConfig]);

  const fetchPreview = async () => {
    if (!safeConfig.rankPick.formula.trim()) {
      return;
    }

    try {
      // Get auth token from Supabase
      const { default: supabase } = await import('../../supabase');
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        console.warn('No session for preview');
        return;
      }

      const response = await fetch(`${apiBaseUrl}/api/screens/preview-selection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          universe: universeConfig,
          lag_days: universeConfig?.fundamental_lag_days ?? 0,
          formula: safeConfig.rankPick.formula,
          selection: safeConfig
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPreview(data);
      }
    } catch (error) {
      console.error('Preview fetch error:', error);
    }
  };

  const handleAddGroup = () => {
    const newGroup = createFilterGroup();
    onChange({
      ...safeConfig,
      filters: {
        groups: [...safeConfig.filters.groups, newGroup]
      }
    });
  };

  const handleUpdateGroup = (groupId: string, updated: FilterGroup) => {
    onChange({
      ...safeConfig,
      filters: {
        groups: safeConfig.filters.groups.map(g =>
          g.id === groupId ? updated : g
        )
      }
    });
  };

  const handleRemoveGroup = (groupId: string) => {
    onChange({
      ...safeConfig,
      filters: {
        groups: safeConfig.filters.groups.filter(g => g.id !== groupId)
      }
    });
  };

  const handleUpdateRankPick = (updated: RankPickSpec) => {
    onChange({
      ...safeConfig,
      rankPick: updated
    });
  };

  return (
    <div className="space-y-6">
      {/* Execution Flow Indicator */}
      <Card className="bg-gradient-to-r from-blue-50 to-green-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
              <span className="font-semibold text-gray-800">Formulas Computed</span>
            </div>
            <div className="text-gray-400 text-2xl">→</div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
              <span className="font-semibold text-gray-800">Filters Applied</span>
            </div>
            <div className="text-gray-400 text-2xl">→</div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
              <span className="font-semibold text-gray-800">Rank & Pick</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview Status Bar */}
      {preview && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <span className="text-sm text-gray-600">Universe: </span>
                  <span className="font-semibold">{preview.universeCount}</span>
                </div>
                <div className="text-gray-400">→</div>
                <div>
                  <span className="text-sm text-gray-600">After Filters: </span>
                  <span className="font-semibold text-blue-600">{preview.survivorCount}</span>
                </div>
                <div className="text-gray-400">→</div>
                <div>
                  <span className="text-sm text-gray-600">Picked: </span>
                  <span className="font-semibold text-green-600">{preview.pickedCount}</span>
                </div>
              </div>
              {preview.topSample.length > 0 && (
                <div className="text-sm text-gray-600">
                  Sample: {preview.topSample.join(', ')}
                </div>
              )}
            </div>
            {preview.explanations.length > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                {preview.explanations.map((exp, i) => (
                  <div key={i}>• {exp}</div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters Section */}
      <Card className="border-l-4 border-l-purple-600">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                <CardTitle>Filters</CardTitle>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                <strong>Step 2:</strong> Apply hard rules using formulas from Step 1 to filter the universe (optional)
              </p>
            </div>
            <button
              onClick={handleAddGroup}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              <Plus className="h-4 w-4" />
              Add Filter Group
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {safeConfig.filters.groups.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-gray-500">No filters - all assets in universe will be ranked</p>
            </div>
          ) : (
            <div className="space-y-4">
              {safeConfig.filters.groups.map((group, index) => (
                <div key={group.id}>
                  {index > 0 && (
                    <div className="flex items-center justify-center py-2">
                      <Badge variant="secondary" className="bg-gray-600 text-white">
                        AND
                      </Badge>
                    </div>
                  )}
                  <FilterGroupEditor
                    group={group}
                    onChange={(updated) => handleUpdateGroup(group.id, updated)}
                    onRemove={() => handleRemoveGroup(group.id)}
                    formulas={formulas}
                  />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rank & Pick Section */}
      <Card className="border-l-4 border-l-green-600">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
            <CardTitle>Rank & Pick</CardTitle>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            <strong>Step 3:</strong> Rank survivors by a formula and select top/bottom performers
          </p>
        </CardHeader>
        <CardContent>
          <RankPickEditor
            spec={safeConfig.rankPick}
            onChange={handleUpdateRankPick}
            formulas={formulas}
            onboardingStep={onboardingStep}
            onOnboardingComplete={onOnboardingComplete}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default SelectionEditorV2;
